/**
 * T+ Paper Trading Panel — Auto Demo positions
 */
import React from 'react';
import {
  Box, Typography, alpha,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { TrendingUp, CheckCircle, Speed } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const thSx = { fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.02em', py: 0.8, borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc' };
const tdSx = { py: 0.7, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9' };

const MetricTile = ({ label, value, color }) => (
  <Box sx={{ flex: 1, minWidth: 80, textAlign: 'center', py: 1, px: 1 }}>
    <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', color: '#94a3b8', letterSpacing: '0.5px' }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.25rem', fontWeight: 900, color: color || '#1e293b', fontFamily: 'monospace', mt: 0.2 }}>{value}</Typography>
  </Box>
);

const PaperTradingPanel = ({ paperPositions, formatNumber, formatPercent }) => {
  const { t } = useTranslation();
  const { active, closed, metrics } = paperPositions;

  return (
    <Box sx={{ p: 2 }}>
      {/* Info banner */}
      <Box sx={{ px: 2, py: 1, mb: 2, borderRadius: 1.5, bgcolor: alpha('#8b5cf6', 0.04), border: `1px solid ${alpha('#8b5cf6', 0.12)}` }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#8b5cf6' }}>
          {t('tplus.paper.banner')}
        </Typography>
      </Box>

      {/* Metrics row */}
      {metrics && (
        <Box sx={{ display: 'flex', borderRadius: 2, border: '1px solid #e2e8f0', overflow: 'hidden', mb: 2, '& > *:not(:last-child)': { borderRight: '1px solid #f1f5f9' } }}>
          <MetricTile label={t('tplus.paper.total')} value={metrics.total_signals} />
          <MetricTile label={t('tplus.paper.active')} value={metrics.active_count} color="#3b82f6" />
          <MetricTile label={t('tplus.paper.closed')} value={metrics.closed_count} />
          <MetricTile label="Win Rate" value={formatPercent(metrics.win_rate, 1)} color={metrics.win_rate >= 50 ? '#10b981' : '#ef4444'} />
          <MetricTile label="Avg P&L" value={`${metrics.avg_return_pct >= 0 ? '+' : ''}${formatPercent(metrics.avg_return_pct, 2)}`} color={metrics.avg_return_pct >= 0 ? '#10b981' : '#ef4444'} />
          <MetricTile label={t('tplus.paper.avgDays')} value={`${metrics.avg_days_held.toFixed(1)}d`} />
        </Box>
      )}

      {/* Active positions */}
      {active.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <TrendingUp sx={{ fontSize: 16, color: '#3b82f6' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
              {t('tplus.paper.activeSection')}
            </Typography>
            <Box sx={{ px: 0.7, py: 0.1, borderRadius: 1, bgcolor: alpha('#3b82f6', 0.08) }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, fontFamily: 'monospace', color: '#3b82f6' }}>{active.length}</Typography>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
                  <TableCell sx={thSx}>{t('tplus.header.entry')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('tplus.header.entryPrice')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('tplus.header.current')}</TableCell>
                  <TableCell align="right" sx={thSx}>P&L</TableCell>
                  <TableCell align="center" sx={thSx}>T+</TableCell>
                  <TableCell align="right" sx={thSx}>Stop</TableCell>
                  <TableCell align="right" sx={thSx}>TP1</TableCell>
                  <TableCell align="right" sx={thSx}>TP2</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {active.map((p) => {
                  const pnlClr = p.unrealized_pnl_pct >= 0 ? '#10b981' : '#ef4444';
                  return (
                    <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{p.ticker}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t(`tplus.setup.${p.setup_type}`, p.setup_type)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: '#64748b' }}>{p.entry_date}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{formatNumber(p.entry_price)}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{formatNumber(p.current_price)}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: pnlClr }}>
                        {p.unrealized_pnl_pct >= 0 ? '+' : ''}{formatPercent(p.unrealized_pnl_pct * 100, 2)}
                      </TableCell>
                      <TableCell align="center" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: '#64748b' }}>T+{p.hold_days}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', color: '#ef4444' }}>{formatNumber(p.stop_price)}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', color: '#10b981' }}>{formatNumber(p.tp1_price)}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', color: '#10b981' }}>{formatNumber(p.tp2_price)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Closed positions */}
      {closed.length > 0 && (
        <Box>
          <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <CheckCircle sx={{ fontSize: 16, color: '#10b981' }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
              {t('tplus.paper.closedSection')}
            </Typography>
            <Box sx={{ px: 0.7, py: 0.1, borderRadius: 1, bgcolor: '#f1f5f9' }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, fontFamily: 'monospace', color: '#94a3b8' }}>{closed.length}</Typography>
            </Box>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('tplus.header.ticker')}</TableCell>
                  <TableCell sx={thSx}>{t('tplus.header.entry')}</TableCell>
                  <TableCell sx={thSx}>{t('tplus.header.exit')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('tplus.header.entryPrice')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('tplus.header.exitPrice')}</TableCell>
                  <TableCell align="right" sx={thSx}>P&L</TableCell>
                  <TableCell align="center" sx={thSx}>{t('tplus.header.held')}</TableCell>
                  <TableCell sx={thSx}>{t('tplus.header.reason')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {closed.map((p) => {
                  const pnlClr = p.realized_pnl_pct >= 0 ? '#10b981' : '#ef4444';
                  const reasonClr = (p.exit_reason || '').includes('TP') ? '#10b981' : (p.exit_reason || '').includes('STOP') ? '#ef4444' : '#f59e0b';
                  return (
                    <TableRow key={p.id} sx={{ '&:hover': { bgcolor: '#fafbfc' } }}>
                      <TableCell sx={tdSx}>
                        <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: '#1e293b' }}>{p.ticker}</Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>{t(`tplus.setup.${p.setup_type}`, p.setup_type)}</Typography>
                      </TableCell>
                      <TableCell sx={{ ...tdSx, color: '#64748b' }}>{p.entry_date}</TableCell>
                      <TableCell sx={{ ...tdSx, color: '#64748b' }}>{p.exit_date}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{formatNumber(p.entry_price)}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace' }}>{formatNumber(p.exit_price)}</TableCell>
                      <TableCell align="right" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: pnlClr }}>
                        {p.realized_pnl_pct >= 0 ? '+' : ''}{formatPercent(p.realized_pnl_pct * 100, 2)}
                      </TableCell>
                      <TableCell align="center" sx={{ ...tdSx, fontFamily: 'monospace', fontWeight: 700, color: '#64748b' }}>{p.hold_days}d</TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: reasonClr }}>{p.exit_reason}</Typography></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Empty state */}
      {active.length === 0 && closed.length === 0 && (
        <Box sx={{ py: 5, px: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f8fafc', border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Speed sx={{ fontSize: 24, color: '#cbd5e1' }} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: '#94a3b8' }}>
              {t('tplus.paper.emptyTitle')}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1', mt: 0.3, maxWidth: 400 }}>
              {t('tplus.paper.emptyDesc')}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PaperTradingPanel;
